import { Provider } from '@angular/core';
import { injectDispatch } from '@ngrx/signals/events';
import { TransactionEvents } from '@app/domain/events';
import { ExpenseDomainService } from '@app/domain/services';
import {
  GetExpensesUseCase,
  GetExpenseByIdUseCase,
  GetExpensesSummaryUseCase,
  GetExpensesTotalAmountUseCase,
  CountExpensesUseCase,
  CreateExpenseUseCase,
  UpdateExpenseUseCase,
  DeleteExpenseUseCase,
  MergeExpensesUseCase,
  ExportExpensesUseCase,
  SuggestCategoriesUseCase,
  BulkSetExpenseCategoryUseCase,
  BulkAddExpenseTagsUseCase,
  SplitExpenseUseCase,
} from '@app/domain/use-cases';
import type {
  IExpenseRepository,
  ICategoryRepository,
  IIncomeRepository,
  ITransactionCategorizationPort,
  ITransactionManager,
  IExternalTransactionRepository,
} from '@app/domain/ports';
import {
  EXPENSE_REPOSITORY,
  INCOME_REPOSITORY,
  CATEGORY_REPOSITORY,
  TRANSACTION_CATEGORIZATION,
  SUGGEST_CATEGORIES_USE_CASE,
  TRANSACTION_MANAGER,
  EXTERNAL_TRANSACTION_REPOSITORY,
} from '../../tokens/infrastructure.tokens';

/**
 * Provides Expense domain services and use cases.
 *
 * Includes:
 * - ExpenseDomainService: Business rules and validation
 * - 7 Use Cases: GetExpenses, GetExpenseById, GetTotal, Count, Create, Update, Delete
 *
 * All providers use constructor injection pattern following Clean Architecture.
 */
export function provideExpenseDomain(): Provider[] {
  return [
    // Domain Service
    {
      provide: ExpenseDomainService,
      useFactory: () => new ExpenseDomainService(),
      deps: [],
    },

    // Use Cases - Query
    {
      provide: GetExpensesUseCase,
      useFactory: (repository: IExpenseRepository) => new GetExpensesUseCase(repository),
      deps: [EXPENSE_REPOSITORY],
    },
    {
      provide: GetExpenseByIdUseCase,
      useFactory: (repository: IExpenseRepository) => new GetExpenseByIdUseCase(repository),
      deps: [EXPENSE_REPOSITORY],
    },
    {
      provide: GetExpensesSummaryUseCase,
      useFactory: (repository: IExpenseRepository) => new GetExpensesSummaryUseCase(repository),
      deps: [EXPENSE_REPOSITORY],
    },
    {
      provide: GetExpensesTotalAmountUseCase,
      useFactory: (repository: IExpenseRepository) => new GetExpensesTotalAmountUseCase(repository),
      deps: [EXPENSE_REPOSITORY],
    },
    {
      provide: CountExpensesUseCase,
      useFactory: (repository: IExpenseRepository) => new CountExpensesUseCase(repository),
      deps: [EXPENSE_REPOSITORY],
    },

    // Use Cases - Commands
    {
      provide: CreateExpenseUseCase,
      useFactory: (repository: IExpenseRepository, domainService: ExpenseDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new CreateExpenseUseCase(repository, domainService, dispatch);
      },
      deps: [EXPENSE_REPOSITORY, ExpenseDomainService],
    },
    {
      provide: UpdateExpenseUseCase,
      useFactory: (repository: IExpenseRepository, domainService: ExpenseDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new UpdateExpenseUseCase(repository, domainService, dispatch);
      },
      deps: [EXPENSE_REPOSITORY, ExpenseDomainService],
    },
    {
      provide: DeleteExpenseUseCase,
      useFactory: (repository: IExpenseRepository, domainService: ExpenseDomainService) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new DeleteExpenseUseCase(repository, domainService, dispatch);
      },
      deps: [EXPENSE_REPOSITORY, ExpenseDomainService],
    },
    {
      provide: MergeExpensesUseCase,
      useFactory: (repository: IExpenseRepository) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new MergeExpensesUseCase(repository, dispatch);
      },
      deps: [EXPENSE_REPOSITORY],
    },
    {
      provide: SplitExpenseUseCase,
      useFactory: (
        repository: IExpenseRepository,
        externalTransactionRepository: IExternalTransactionRepository,
        transactionManager: ITransactionManager,
        domainService: ExpenseDomainService,
      ) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new SplitExpenseUseCase(
          repository,
          externalTransactionRepository,
          transactionManager,
          domainService,
          dispatch,
        );
      },
      deps: [EXPENSE_REPOSITORY, EXTERNAL_TRANSACTION_REPOSITORY, TRANSACTION_MANAGER, ExpenseDomainService],
    },
    {
      provide: ExportExpensesUseCase,
      useFactory: (repository: IExpenseRepository) => new ExportExpensesUseCase(repository),
      deps: [EXPENSE_REPOSITORY],
    },
    {
      provide: BulkSetExpenseCategoryUseCase,
      useFactory: (repository: IExpenseRepository) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new BulkSetExpenseCategoryUseCase(repository, dispatch);
      },
      deps: [EXPENSE_REPOSITORY],
    },
    {
      provide: BulkAddExpenseTagsUseCase,
      useFactory: (repository: IExpenseRepository, transactionManager: ITransactionManager) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new BulkAddExpenseTagsUseCase(repository, transactionManager, dispatch);
      },
      deps: [EXPENSE_REPOSITORY, TRANSACTION_MANAGER],
    },
    {
      provide: SUGGEST_CATEGORIES_USE_CASE,
      useFactory: (
        categorization: ITransactionCategorizationPort,
        categoryRepo: ICategoryRepository,
        expenseRepo: IExpenseRepository,
        incomeRepo: IIncomeRepository,
      ) => new SuggestCategoriesUseCase(categorization, categoryRepo, expenseRepo, incomeRepo),
      deps: [TRANSACTION_CATEGORIZATION, CATEGORY_REPOSITORY, EXPENSE_REPOSITORY, INCOME_REPOSITORY],
    },
  ];
}
